<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { infoBoxIdEnum, infoBoxToneEnum, serviceTypeIcons } from '@/stores/portal.store'
import { useScheduleStore } from '@/stores/schedule.store'
import { useChips } from '@/composables/chips'
import { signalToneColor, signalToneEnum } from '@/composables/dashboard-kpi'
import InfoBox from '@/components/shared/InfoBox.vue'
import ListItem from '@/components/shared/ListItem.vue'
import SearchInput from '@/components/shared/SearchInput.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['replace', 'cancel'])

const { t } = useI18n()
const planningStore = usePlanningStore()
const scheduleStore = useScheduleStore()
const { getServiceTagChips } = useChips()

const warningColor = signalToneColor(signalToneEnum.ATTENTION)

const candidates = ref([])
const loading = ref(false)
const search = ref('')
const selectedId = ref(null)

const onShow = async () => {
  search.value = ''
  selectedId.value = null
  candidates.value = []
  loading.value = true
  try {
    const { data } = await scheduleStore.fetchReplacementCandidatesAsync(props.modelValue.row.id)
    candidates.value = data
      .map(candidate => ({ ...candidate, service: planningStore.getServiceById(candidate.service_id) }))
      .filter(candidate => candidate.service)
  } finally {
    loading.value = false
  }
}

const tagChips = (service) => getServiceTagChips(service.tags)

const matches = (candidate) => {
  const query = search.value?.trim().toLowerCase()
  if (!query) return true
  return planningStore.getServiceName(candidate.service).toLowerCase().includes(query)
    || tagChips(candidate.service).some(chip => chip.label?.toLowerCase().includes(query))
}

const byAvailabilityThenName = (a, b) =>
  Number(b.available && !b.in_project) - Number(a.available && !a.in_project)
  || planningStore.getServiceName(a.service).localeCompare(planningStore.getServiceName(b.service))

const sections = computed(() => {
  const visible = candidates.value.filter(matches).sort(byAvailabilityThenName)
  return [
    { key: 'peers', label: t('common.suggestedServices'), items: visible.filter(c => c.peer) },
    { key: 'others', label: t('common.otherServices'), items: visible.filter(c => !c.peer) },
  ].filter(section => section.items.length)
})

const statusChip = (candidate) => {
  if (candidate.in_project) return { label: t('common.alreadyInProject'), color: 'grey-5' }
  if (candidate.available) return { label: t('common.free'), color: 'positive' }
  return { label: t('common.busy'), color: warningColor }
}

const select = (candidate) => {
  if (!candidate.in_project) selectedId.value = candidate.service_id
}
</script>

<template>
  <q-dialog
    :model-value="props.modelValue !== null"
    class="dialog-xs"
    @show="onShow"
    @hide="emit('cancel')"
  >
    <q-card v-if="props.modelValue" class="replace-dialog">
      <q-card-section class="row items-center no-wrap q-gutter-sm">
        <q-avatar
          icon="mdi-swap-horizontal"
          color="primary"
          text-color="white"
          size="md"
        />
        <div class="col">
          <div class="text-h6">
            {{ $t('common.replaceService') }}
          </div>
          <div class="text-caption text-grey-7 ellipsis">
            {{ props.modelValue.title }}
          </div>
        </div>
      </q-card-section>

      <q-card-section class="q-pt-none column q-gutter-y-sm">
        <info-box :id="infoBoxIdEnum.REPLACE_SERVICE" :message="$t('common.replaceServiceHint')" />
        <info-box
          v-if="props.modelValue.confirmed"
          :tone="infoBoxToneEnum.WARNING"
          :message="$t('common.replaceConfirmedWarning')"
        />
        <search-input v-model="search" autofocus />
      </q-card-section>

      <q-card-section class="q-pt-none replace-dialog__list">
        <div v-if="loading" class="flex flex-center q-pa-lg">
          <q-spinner color="primary" size="md" />
        </div>
        <div v-else-if="!sections.length" class="text-grey-6 text-center q-pa-md">
          {{ $t('common.noResults') }}
        </div>
        <q-list v-else>
          <template v-for="section in sections" :key="section.key">
            <q-item-label header class="replace-dialog__section">
              {{ section.label }}
            </q-item-label>
            <q-item
              v-for="candidate in section.items"
              :key="candidate.service_id"
              :clickable="!candidate.in_project"
              :active="selectedId === candidate.service_id"
              active-class="replace-dialog__item--active"
              :class="['q-pa-sm', 'replace-dialog__item', { 'replace-dialog__item--disabled': candidate.in_project }]"
              @click="select(candidate)"
            >
              <list-item
                dense
                :avatar-icon="serviceTypeIcons[candidate.service.type]"
                :label="planningStore.getServiceName(candidate.service)"
                :caption-chips="tagChips(candidate.service)"
              >
                <template #right>
                  <q-chip
                    dense
                    square
                    :color="statusChip(candidate).color"
                    text-color="white"
                    :label="statusChip(candidate).label"
                    class="replace-dialog__status"
                  />
                </template>
              </list-item>
            </q-item>
          </template>
        </q-list>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat :label="$t('common.cancel')" @click="emit('cancel')" />
        <q-btn
          color="primary"
          icon="mdi-swap-horizontal"
          :label="$t('common.replace')"
          :disable="!selectedId"
          @click="emit('replace', selectedId)"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style scoped lang="scss">
.replace-dialog__list {
  max-height: 50vh;
  overflow-y: auto;
}

.replace-dialog__section {
  padding: 0.5rem 0.5rem 0.25rem;
  font-weight: 600;
}

.replace-dialog__item {
  border-radius: 0.5rem;
}

.replace-dialog__item--active {
  background: color-mix(in srgb, var(--q-primary) 10%, transparent);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--q-primary) 40%, transparent);
}

.replace-dialog__item--disabled {
  opacity: 0.55;
}

.replace-dialog__status {
  margin: 0;
  font-size: 0.6875rem;
}
</style>
